import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
  ViewChild,
} from '@angular/core';
import { FilterProperty } from '../../../search/filter-property';
import { SelectionsService } from '../../../shared/services/selections.service';
import { TranslateService } from '@ngx-translate/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { ReplaySubject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { list } from '../edit-action/dynamicTagsList';
import { AutomationHelpers } from '../../automation-helpers';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { CurrentSiteIdStore } from 'app/core/current-site-id.store';
import { TriggerSubType, TriggerTypeArgument } from 'app/automations/automation-helpers-actions';
import { EventScopeOption } from 'app/automations/automations-models';

@Component({
  selector: 'app-dynamic-field',
  templateUrl: './dynamic-field.component.html',
  styleUrls: ['./dynamic-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DynamicFieldComponent implements OnInit, OnDestroy {
  public suggestions: FilterProperty[] | any;
  @Input() isAction: boolean;
  @Input() actionId: string;
  @Input() type: string;
  @Input() value: string;
  @Input() placeHolder: string;
  @Input() required: boolean;
  @Input() descriptionTranslationKey: string;
  @Input() descriptionAboveTranslationKey: string;
  @Input() page: string;
  @Input() widthField: string;
  @Output() valueChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() urlChanged: EventEmitter<any> = new EventEmitter<any>();
  @Output() textChanged: EventEmitter<any> = new EventEmitter<any>();

  public showDropdown = false;
  public showGroupedDropdown = false;
  public showAutocomplete = false;
  public hideDropdownSearch = false;
  public suggestionsFilterCtrl: UntypedFormControl = new UntypedFormControl();
  public filteredSuggestions: ReplaySubject<FilterProperty[]> = new ReplaySubject<FilterProperty[]>(1);
  public dropdownData: Array<{ value: string; label: string; group: string }>;
  public eventScopes: Array<EventScopeOption> = [];
  public eventScopesLoading = false;
  public filteredEventScopes: Array<EventScopeOption> = [];
  public eventScopeFilterCtrl: UntypedFormControl = new UntypedFormControl('');
  // valueChanges and the url subject never complete, so they outlive the component unless dropped here.
  private readonly subscriptions = new Subscription();
  public selectedEventScope: string;
  public eventSubType: string = TriggerSubType.CouponDistributed;
  public selectedValue: any;
  public _operator: string;
  private urlChangedSubject: ReplaySubject<any> = new ReplaySubject<any>();

  @Input() set operator(val: any) {
    this._operator = val;
    switch (this._operator) {
      case 'Contains':
        this.showDropdown = false;
        break;
      case 'StartsWith':
        this.showDropdown = false;
        break;
      case 'EndsWith':
        this.showDropdown = false;
        break;
      default:
        this.showDropdown = this.isAction || this.type !== null;
    }

    const ignoreTypes = ['TextArea', 'TextField', 'Checkbox', 'Number', 'Description', 'VisitedPage'];
    if (!ignoreTypes.includes(this.type) && !this.showDropdown && this.type !== null) {
      this.fetchData().then();
    }
    if (
      ['ContainsAnyOf', 'DoesNotContainsAnyOf', 'Contains'].includes(val) &&
      this.type !== TriggerTypeArgument.VisitedPage
    ) {
      this.placeHolder = 'Strings to match (comma-separated)';
    }
  }

  get operator(): any {
    return this._operator;
  }

  constructor(
    private selectionsService: SelectionsService,
    private translateService: TranslateService,
    private readonly currentSiteIdStore: CurrentSiteIdStore,
  ) {}

  async ngOnInit(): Promise<void> {
    if (
      this.actionId &&
      (this.actionId.endsWith('UpdateMergeTag') ||
        this.actionId.endsWith('UpdateSubscriberField') ||
        this.actionId.endsWith('UpdateField') ||
        this.type === 'PropertyInTriggerbee')
    ) {
      this.showGroupedDropdown = true;
      this.dropdownData = list;
      const field_exist = this.dropdownData.find((item) => item.value === this.value);
      if (!field_exist && this.showGroupedDropdown && this.type === 'TextField') {
        this.dropdownData.push({
          label: this.value,
          value: this.value,
          group: 'Custom',
        });
      }
      this.selectedValue = this.value;
    }
    const ignoreTypes = ['TextArea', 'TextField', 'Checkbox', 'Number', 'Description', 'VisitedPage'];
    if (!ignoreTypes.includes(this.type) && this.type !== null) {
      this.fetchData().then();
      this.showDropdown = this.isAction || this.type !== null;
    }
    const urlChangedSub = this.urlChangedSubject.pipe(debounceTime(100)).subscribe(async (url) => {
      if (this._operator === 'Contains') {
        // A fragment can contain characters that are invalid in a full path, only wildcards are rejected
        if (this.value?.includes('*') && !url.errors) {
          url.control.setErrors({ pattern: true });
        }
      } else {
        // Try to get only path of url
        try {
          const path = new URL(url.value).pathname;
          this.value = path.split('*').join('');
          this.value = this.value.replace(/\/+/g, '/');
        } catch (_) {}

        // Validate
        const pathRegex = new RegExp('^[^*.!: ]+$');
        const testMatch = pathRegex.test(this.value);
        if (!testMatch && !url.errors) {
          url.control.setErrors({ urlPathNotValid: true });
        }
      }

      // Emit events
      this.urlChanged.emit(url);
      await this.onSelectionChange(url);
    });
    this.subscriptions.add(urlChangedSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public async fetchData() {
    const siteId = this.currentSiteIdStore.get() ?? 0;
    switch (this.type) {
      case 'Goal':
      case 'AbandonCart': {
        const widgets = await this.selectionsService.getAllWidgetsSimpleInfosAsync();
        const goals = await this.selectionsService.getGoalsAsync();
        this.suggestions = AutomationHelpers.prettifyGoalsNames(this.translateService, widgets, goals, 'displayName');
        // Goal entities are created lazily from traffic, so a configured argument may not be fetched yet.
        if (this.value && !this.suggestions.some((item) => item.id === this.value)) {
          this.suggestions.push(
            new FilterProperty().set(
              this.value,
              AutomationHelpers.prettifySingleGoalName(this.translateService, widgets, this.value),
            ),
          );
        }
        break;
      }
      case 'Widget': {
        this.suggestions = await this.selectionsService.getWidgetsAsFilterPropertiesAsync();
        break;
      }
      case 'Label': {
        this.suggestions = await this.selectionsService.getLabelsAsync();
        this.showAutocomplete = true;
        this.suggestionsFilterCtrl = new UntypedFormControl('', [Validators.required, Validators.pattern('^[^\\*]+$')]);
        break;
      }
      case 'Person': {
        this.suggestions = await this.selectionsService.getPersonsAsync(siteId);
        this.suggestions = this.suggestions.map((item) => ({
          displayName: item.email,
          id: item.email,
          createdDate: item.createdAt,
        }));
        break;
      }
      case 'PropertyInTriggerbee': {
        this.selectedValue = this.value;
        this.showGroupedDropdown = true;
        this.type = 'TextField';
        break;
      }
      case 'GetZaps': {
        this.suggestions = await this.selectionsService.getZaps();
        break;
      }
      case TriggerTypeArgument.Event: {
        this.eventScopesLoading = true;
        const saved = AutomationHelpers.parseEventOptionValue(this.value);
        this.eventSubType = saved.subType ?? TriggerSubType.CouponDistributed;
        this.selectedEventScope =
          saved.widgetId == null
            ? null
            : AutomationHelpers.buildEventScopeValue(saved.widgetId, saved.componentId ?? null);
        this.subscriptions.add(this.eventScopeFilterCtrl.valueChanges.subscribe(() => this.refreshEventScopes()));
        this.loadEventScopes();
        break;
      }
      default: {
        throw new Error(`Not implemented. Action ID: ${this.actionId}. Type: ${this.type}`);
      }
    }

    // Always include current set value in lists, else we risk loosing the value when we save
    if (!this.suggestions) {
      this.suggestions = [];
      this.suggestions.push(new FilterProperty().set(this.value, this.value));
    }

    if (this.suggestions) {
      this.filteredSuggestions.next(this.suggestions.slice());
    }
    this.subscriptions.add(
      this.suggestionsFilterCtrl.valueChanges.subscribe(() => {
        this.filterSuggestions();
      }),
    );
  }

  public async onSelectionChange(model) {
    if (!this.validateData(this.value)) return;
    this.valueChange.emit(this.value);
    if (model) {
      this.textChanged.emit(model);
    }
  }

  public onCheckboxChange(model: MatSlideToggleChange) {
    this.value = model.checked.toString();
    this.valueChange.emit(this.value);
  }

  public onDropdownSelectChange(model, event) {
    if (model) {
      this.textChanged.emit(model);
      this.valueChange.emit(model);
    } else {
      const value = event.label;
      this.textChanged.emit(value);
      this.valueChange.emit(value);
    }
  }

  // Keeps a saved scope listed even if its component is gone, so editing cannot silently drop it.
  private async loadEventScopes(): Promise<void> {
    this.eventScopesLoading = true;
    this.eventScopes = await this.selectionsService.getEventScopeOptionsAsync(this.selectedEventScope);
    this.refreshEventScopes();
    this.eventScopesLoading = false;
  }

  // Subtype comes from the picked trigger, the dropdown adds the scope; emit only once a scope has been picked.
  public onEventSelectionChange() {
    if (!this.selectedEventScope) {
      return;
    }
    const scope = AutomationHelpers.parseEventScopeValue(this.selectedEventScope);
    const merged = AutomationHelpers.buildEventOptionValue(
      this.eventSubType,
      scope.widgetId,
      scope.componentId ?? null,
    );
    this.textChanged.emit(merged);
    this.valueChange.emit(merged);
  }

  // Rebuilt whenever the list or the search text changes, so the template only reads a property.
  private refreshEventScopes() {
    const search = (this.eventScopeFilterCtrl.value ?? '').toString().trim().toLowerCase();
    this.filteredEventScopes = search
      ? this.eventScopes.filter((scope) => scope.label.toLowerCase().includes(search))
      : [...this.eventScopes];
  }

  public async truncateURL(url) {
    await this.urlChangedSubject.next(url);
  }

  getInputErrorMessage(input) {
    return input.hasError('required')
      ? this.translateService.instant('generic.validation.error-required')
      : input.hasError('urlPathNotValid')
        ? this.translateService.instant('generic.validation.error-url-path-not-valid')
        : input.hasError('pattern')
          ? this.translateService.instant('generic.validation.error-invalid-chars')
          : input.hasError('minlength')
            ? this.translateService.instant('generic.validation.error-minlength-4')
            : '';
  }

  getHint(type) {
    if (type === 'url') {
      return this.translateService.instant('generic.validation.error-url-path-not-valid');
    } else {
      if (['ContainsAnyOf', 'DoesNotContainsAnyOf', 'Contains'].includes(this.operator)) {
        return 'String that match any of the strings in your field below (comma-separated)';
      }
    }
  }

  protected filterSuggestions() {
    if (!this.suggestions) {
      return;
    }
    let search = this.suggestionsFilterCtrl.value;
    if (!search) {
      this.filteredSuggestions.next(this.suggestions.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    this.filteredSuggestions.next(
      this.suggestions.filter((item) => item.displayName.toLowerCase().indexOf(search.toLowerCase()) > -1),
    );
  }

  public cancelPropagationOnLastPassExtension(event) {
    if (event.which === 13 || event.keyCode === 13 || event.key === 'Enter') {
      event.stopPropagation();
    }
  }

  private validateData(value) {
    if (this.type === 'Number') {
      if (value < 0) {
        return;
      }
    }
    return true;
  }

  get checkIfHavePrefilledValue() {
    return (
      this.suggestions &&
      this.suggestions.length &&
      this.type === TriggerTypeArgument.AbandonCart &&
      !this.suggestions.some((item) => item.id === this.value)
    );
  }
}
